import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Spinner, Table } from "react-bootstrap";

import ProctoringAdminService from "../../_services/ProctoringAdminService";
import WebProctoringPanel from "../quick-quiz-maker/WebProctoringPanel";

/**
 * Blocks tab of the Proctoring Center — every browser-proctoring block lever
 * in one place. Lists the latest block state per (test, student) from
 * test_proctored_blocks (auto violation-blocks + admin blocks), lets the
 * admin lift or re-apply them, and drills into the webcam-AI evidence panel
 * (which carries its own Test Block / Blanket Block DynamoDB levers) for the
 * same student without leaving the page.
 */

interface BlockRow {
  TestId: number;
  testName: string;
  EnquiryId: number;
  studentName: string;
  blockType: string;
  status: string;
  reason: string | null;
  entryDate: string | null;
  totalViolations: number;
  tabSwitches: number;
  fullscreenExits: number;
}

const chip = (bg: string, color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

const BlocksPanel: React.FC = () => {
  const [rows, setRows] = useState<BlockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [testFilter, setTestFilter] = useState("");
  const [actionTarget, setActionTarget] = useState<{
    row: BlockRow;
    action: "unblock" | "block";
  } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [acting, setActing] = useState(false);
  const [webcamTarget, setWebcamTarget] = useState<BlockRow | null>(null);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ProctoringAdminService.getBlocks(
        testFilter ? Number(testFilter) : undefined,
      );
      setRows(res.data.data.blocks || []);
    } catch (err) {
      console.error("Failed to fetch blocks:", err);
    } finally {
      setLoading(false);
    }
  }, [testFilter]);

  useEffect(() => {
    fetchBlocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAction = async () => {
    if (!actionTarget) return;
    setActing(true);
    const { row, action } = actionTarget;
    try {
      if (action === "unblock") {
        await ProctoringAdminService.unblockStudent({
          testId: row.TestId,
          enquiryId: row.EnquiryId,
          reason: actionReason || undefined,
        });
      } else {
        await ProctoringAdminService.blockStudent({
          testId: row.TestId,
          enquiryId: row.EnquiryId,
          reason: actionReason || "Re-blocked by admin",
        });
      }
      setActionTarget(null);
      setActionReason("");
      fetchBlocks();
    } catch (err) {
      console.error(`Failed to ${action} student:`, err);
    } finally {
      setActing(false);
    }
  };

  if (webcamTarget) {
    return (
      <WebProctoringPanel
        testId={webcamTarget.TestId}
        enquiryId={webcamTarget.EnquiryId}
        onBack={() => setWebcamTarget(null)}
      />
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <h5 style={{ margin: 0 }}>Proctoring Blocks</h5>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 140 }}
            placeholder="Filter by Test ID"
            value={testFilter}
            onChange={(e) => setTestFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchBlocks()}
          />
          <Button size="sm" variant="primary" onClick={fetchBlocks}>
            Refresh
          </Button>
        </div>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
        Latest block state per student and test. Automatic blocks are written
        when a student crosses the category's tab-switch or fullscreen-exit
        limits; unblocking grants a fresh allowance. The Webcam button opens
        the webcam-AI evidence for the same student, including its separate
        Test Block / Blanket Block controls.
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner animation="border" size="sm" /> Loading blocks...
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "var(--text-muted)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
          }}
        >
          No blocks on record{testFilter ? " for this test" : ""}. Students are
          auto-blocked here when they exceed the category's violation limits.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table striped bordered hover size="sm" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Student</th>
                <th>Test</th>
                <th style={{ textAlign: "center" }}>Status</th>
                <th style={{ textAlign: "center" }}>Type</th>
                <th style={{ textAlign: "center" }}>Violations (Tab/FS)</th>
                <th>Reason</th>
                <th>When</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const blocked = r.status === "BLOCK";
                return (
                  <tr key={`${r.TestId}-${r.EnquiryId}`}>
                    <td>{r.studentName}</td>
                    <td>
                      {r.testName}{" "}
                      <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                        #{r.TestId}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {blocked ? (
                        <span style={chip("#fee2e2", "#b91c1c")}>Blocked</span>
                      ) : (
                        <span style={chip("#dcfce7", "#15803d")}>Unblocked</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {r.blockType === "VIOLATION_AUTO" ? (
                        <span style={chip("#fef3c7", "#92400e")}>Auto</span>
                      ) : (
                        <span style={chip("#e0e7ff", "#3730a3")}>Admin</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {r.totalViolations}{" "}
                      <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                        ({r.tabSwitches}/{r.fullscreenExits})
                      </span>
                    </td>
                    <td
                      style={{
                        maxWidth: 260,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={r.reason || ""}
                    >
                      {r.reason || "—"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {r.entryDate ? new Date(r.entryDate).toLocaleString() : "—"}
                    </td>
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      {blocked ? (
                        <Button
                          size="sm"
                          variant="success"
                          style={{ marginRight: 4, fontSize: 11, padding: "2px 8px" }}
                          onClick={() =>
                            setActionTarget({ row: r, action: "unblock" })
                          }
                        >
                          Unblock
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="danger"
                          style={{ marginRight: 4, fontSize: 11, padding: "2px 8px" }}
                          onClick={() =>
                            setActionTarget({ row: r, action: "block" })
                          }
                        >
                          Re-block
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        onClick={() => setWebcamTarget(r)}
                      >
                        Webcam
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={!!actionTarget} onHide={() => setActionTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {actionTarget?.action === "unblock" ? "Unblock Student" : "Block Student"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14 }}>
            {actionTarget?.action === "unblock" ? "Unblock" : "Block"}{" "}
            <strong>{actionTarget?.row.studentName}</strong> on{" "}
            <strong>{actionTarget?.row.testName}</strong>?
          </p>
          {actionTarget?.action === "unblock" && (
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
              The student can relaunch the test immediately and gets a fresh
              violation allowance — earlier violations stay on record but no
              longer count toward the limit.
            </p>
          )}
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Reason (optional)"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setActionTarget(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={actionTarget?.action === "unblock" ? "success" : "danger"}
            onClick={handleAction}
            disabled={acting}
          >
            {acting
              ? "Working..."
              : actionTarget?.action === "unblock"
                ? "Confirm Unblock"
                : "Confirm Block"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlocksPanel;
